<template>
  <nx-fullscreen-dialog
    :active="active"
    :title="$t('message.clipping.clipper.title')"
    @dismiss="dismiss"
    back
    classes="nx-clipper nx-primary--lighten-4"
    dismissable
  >
    <template slot="toolbar-items">
      <v-btn
        @click.native="crop"
        color="accent"
        flat dark
        :disabled="disabled"
        :loading="loading"
      >{{ $t('message.clipping.clipper.accept') }}</v-btn>
    </template>

    <v-container pa-0 fluid class="nx-clipper-body">
        <v-flex class="nx-clipper" v-if="src">

          <vue-cropper
            v-if="active"
            ref="cropper"
            :container-style="containerStyle"
            :crop="updateDetails"
            :aspect-ratio="aspectRatio"
            :auto-crop="false"
            :check-cross-origin="false"
            drag-mode="crop"
            :responsive="true"
            :view-mode="2"
            :zoomable="false"
            :src="src"
          ></vue-cropper>

          <v-toolbar dark dense elevation-3 card v-if="currentRatio">
            <v-tooltip top>
              <v-btn icon @click="clear" slot="activator" color="accent--text">
                <v-icon>cancel</v-icon>
              </v-btn>
              <span v-html="$t('message.clipping.clipper.cancel')"></span>
            </v-tooltip>
            <v-menu>
              <v-btn slot="activator" flat>
                <span v-text="aspectRatioDescription(currentRatio)"></span>
                <v-icon>arrow_drop_down</v-icon>
              </v-btn>
              <v-list>
                <v-list-tile
                  @click="setAspectRatio(ratio)"
                  v-for="ratio in ratios"
                  :key="ratio.alias">
                  <v-list-tile-content>
                    <v-list-tile-title
                      :class="{ 'accent--text': currentRatio.alias === ratio.alias }"
                      v-text="aspectRatioDescription(ratio)">
                    </v-list-tile-title>
                  </v-list-tile-content>
                </v-list-tile>
              </v-list>
            </v-menu>
            <v-chip color="transparent" class="grey--text" disabled style="width: 90px;">
              {{ width }} x {{ height }}
            </v-chip>
          </v-toolbar>
        </v-flex>
    </v-container>
  </nx-fullscreen-dialog>
</template>

<script>
import Loadable from '@/mixins/loadable';
import VueCropper from 'vue-cropperjs';
import FullscreenDialog from '@/components/dialogs/FullscreenDialog';
import { mapGetters } from 'vuex';

export default {
  name: 'nx-clipping-tool-cropper',
  extends: FullscreenDialog,
  mixins: [Loadable],
  components: {
    VueCropper,
    [FullscreenDialog.name]: FullscreenDialog,
  },
  props: {
    src: {
      type: String,
      required: true,
    },
    aspectRatios: {
      type: Array,
      required: true,
    },
    allowOnlySourceAspectRatio: Boolean,
  },
  data() {
    return {
      cropper: null,
      currentRatio: {
        width: 1,
        height: 1,
      },
      height: 0,
      width: 0,
    };
  },
  computed: {
    ...mapGetters(['availableClippingAspectRatios', 'availableClippingAspectRatioAliases', 'beforeCreateClippingAspectRatios']),
    containerStyle() {
      return {
        width: '100%',
        height: '100%',
        overflow: 'hidden',
      };
    },
    ratios() {
      return (this.allowOnlySourceAspectRatio
        ? this.availableClippingAspectRatioAliases
        : this.aspectRatios
      )
        .map((ratio) => {
          if (typeof ratio === 'string') {
            return this.availableClippingAspectRatios[ratio] || this.beforeCreateClippingAspectRatios[ratio];
          }
          return ratio;
        })
        .filter(ratio => !!ratio);
    },
    aspectRatio() {
      return this.currentRatio.width / this.currentRatio.height;
    },
    disabled() {
      return !this.width || !this.height;
    },
  },
  methods: {
    clear() {
      this.$refs.cropper.clear();
      this.width = 0;
      this.height = 0;
    },
    async crop() {
      this.loadingStart();
      this.$refs.cropper.disable();

      const data = this.$refs.cropper.getData();
      ['x', 'y', 'width', 'height'].forEach((k) => {
        data[k] = Math.round(data[k]);
      });

      const crop = {
        aspectRatio: this.allowOnlySourceAspectRatio
          ? this.availableClippingAspectRatios['source-aspect-ratio'].alias
          : this.currentRatio.alias,
        width: data.width,
        height: data.height,
        operations: [{
          op: 'crop',
          params: {
            x1: data.x,
            y2: data.y + data.height,
            x2: data.x + data.width,
            y1: data.y,
          },
        }],
      };

      this.$emit('crop', crop, this.$refs.cropper.getCroppedCanvas());
    },
    reset() {
      this.$refs.cropper.enable();
      this.loadingFinish();
    },
    dismiss() {
      this.clear();
      this.$emit('dismiss');
    },
    setAspectRatio(ratio) {
      this.currentRatio = ratio;
      if (this.$refs.cropper) {
        this.$refs.cropper.setAspectRatio(ratio.width / ratio.height);
      }
    },
    updateDetails({ detail }) {
      this.width = (detail && Math.round(detail.width)) || 0;
      this.height = (detail && Math.round(detail.height)) || 0;
    },
    aspectRatioDescription({ width, height, description }) {
      if (description) {
        return description;
      }
      return `${width}:${height}`;
    },
  },
  mounted() {
    this.setAspectRatio(this.ratios[0]);
  },
};
</script>

<style lang="stylus">
.nx-clipper {
  height: calc(100vh - 64px)
  .nx-clipper-body {
    .v-toolbar {
      position: absolute;
      bottom: 0;
      left: 50%;
      width: auto;
      transform: translateX(-50%) !important;
      transition: bottom .5s;
    }

    .cropper-container.cropper-bg {
      background-image: none
    }
  }
}
</style>
